import { CognitoUserPool } from 'amazon-cognito-identity-js';
import config from 'config';
import { BrowserStorage } from 'utils/browserStorage';
import { isTokenExpired } from 'app/modules/auth/utils/jwtUtils';
import Logger from 'utils/logger';

const getAccountNameFromHostname = (): string | null => {
  const hostname = window.location.hostname;
  const baseDomain =
    config.MODE === 'production' ? 'kontentino.com' : 'dev-ec1.kontentino.dev';

  if (!hostname.endsWith(baseDomain)) {
    return null;
  }

  const accountName = hostname.replace(`.${baseDomain}`, '');
  return accountName || null;
};

export interface SelectedAccount {
  accountId: number;
  accountName: string;
  userRole: string;
  userName: string;
}

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

const AUTH_TOKEN_COOKIE = 'auth-token';
const ACCOUNT_ID_COOKIE = 'account-id';

function setCookie(name: string, value: string, days = 7): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

function removeCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

let cachedAccessToken: string | null = null;
let cachedIdToken: string | null = null;

const userPool = new CognitoUserPool({
  UserPoolId: config.COGNITO.userPoolId,
  ClientId: config.COGNITO.clientId,
});

export const CognitoTokenManager = {
  getAccessToken(): Promise<string | null> {
    const storedToken = BrowserStorage.get(BrowserStorage.keys.AuthToken);
    Logger.log(
      `[Auth][TokenManager] getAccessToken: storedToken=${
        storedToken ? `present (${storedToken.length} chars)` : 'null'
      }`,
    );

    if (storedToken && !isTokenExpired(storedToken)) {
      cachedAccessToken = storedToken;
      Logger.log('[Auth][TokenManager] getAccessToken: returning valid token');
      return Promise.resolve(storedToken);
    }

    cachedAccessToken = null;
    Logger.warn(
      `[Auth][TokenManager] getAccessToken: returning null (token ${
        storedToken ? 'expired/invalid' : 'missing'
      })`,
    );
    return Promise.resolve(null);
  },

  getAccessTokenSync(): string | null {
    return cachedAccessToken;
  },

  getIdToken(): string | null {
    if (cachedIdToken) {
      return cachedIdToken;
    }
    return BrowserStorage.get(BrowserStorage.keys.Cognito.IdToken);
  },

  setAccessToken(token: string | null): void {
    cachedAccessToken = token;
    if (token) {
      BrowserStorage.set(BrowserStorage.keys.AuthToken, token);
      BrowserStorage.set(BrowserStorage.keys.Cognito.AccessToken, token);
    } else {
      BrowserStorage.remove(BrowserStorage.keys.AuthToken);
      BrowserStorage.remove(BrowserStorage.keys.Cognito.AccessToken);
    }
  },

  setTokens(tokens: CognitoTokens): void {
    Logger.log(
      `[Auth][TokenManager] setTokens: idToken=${
        tokens.idToken ? `${tokens.idToken.length} chars` : 'MISSING'
      }, accessToken=${
        tokens.accessToken ? `${tokens.accessToken.length} chars` : 'MISSING'
      }, refreshToken=${
        tokens.refreshToken ? `${tokens.refreshToken.length} chars` : 'MISSING'
      }`,
    );
    cachedAccessToken = tokens.accessToken;
    cachedIdToken = tokens.idToken;
    BrowserStorage.set(BrowserStorage.keys.AuthToken, tokens.accessToken);
    BrowserStorage.set(BrowserStorage.keys.Cognito.IdToken, tokens.idToken);
    BrowserStorage.set(
      BrowserStorage.keys.Cognito.AccessToken,
      tokens.accessToken,
    );
    BrowserStorage.set(
      BrowserStorage.keys.Cognito.RefreshToken,
      tokens.refreshToken,
    );
    setCookie(AUTH_TOKEN_COOKIE, tokens.accessToken);
  },

  getTokens(): CognitoTokens | null {
    const idToken = BrowserStorage.get(BrowserStorage.keys.Cognito.IdToken);
    const accessToken = BrowserStorage.get(
      BrowserStorage.keys.Cognito.AccessToken,
    );
    const refreshToken = BrowserStorage.get(
      BrowserStorage.keys.Cognito.RefreshToken,
    );

    if (!idToken || !accessToken || !refreshToken) {
      Logger.warn(
        `[Auth][TokenManager] getTokens: incomplete tokens - idToken=${!!idToken}, accessToken=${!!accessToken}, refreshToken=${!!refreshToken}`,
      );
      return null;
    }

    Logger.log('[Auth][TokenManager] getTokens: all tokens present');
    return { idToken, accessToken, refreshToken };
  },

  clearToken(): void {
    cachedAccessToken = null;
    cachedIdToken = null;
    BrowserStorage.remove(BrowserStorage.keys.AuthToken);
    BrowserStorage.remove(BrowserStorage.keys.Cognito.IdToken);
    BrowserStorage.remove(BrowserStorage.keys.Cognito.AccessToken);
    BrowserStorage.remove(BrowserStorage.keys.Cognito.RefreshToken);
    removeCookie(AUTH_TOKEN_COOKIE);
  },

  setSelectedAccount(account: SelectedAccount): void {
    BrowserStorage.set(
      BrowserStorage.keys.Cognito.SelectedAccount,
      JSON.stringify(account),
    );
    setCookie(ACCOUNT_ID_COOKIE, String(account.accountId));
  },

  getSelectedAccount(): SelectedAccount | null {
    const stored = BrowserStorage.get(
      BrowserStorage.keys.Cognito.SelectedAccount,
    );
    if (!stored) {
      return null;
    }
    try {
      return JSON.parse(stored) as SelectedAccount;
    } catch {
      return null;
    }
  },

  getSelectedAccountId(): number | null {
    const account = this.getSelectedAccount();
    return account?.accountId ?? null;
  },

  clearSelectedAccount(): void {
    BrowserStorage.remove(BrowserStorage.keys.Cognito.SelectedAccount);
    removeCookie(ACCOUNT_ID_COOKIE);
  },

  isAuthenticated(): Promise<boolean> {
    const tokens = this.getTokens();

    if (!tokens?.accessToken) {
      return Promise.resolve(false);
    }

    return Promise.resolve(!isTokenExpired(tokens.accessToken));
  },

  signOut(): void {
    Logger.log('[Auth][TokenManager] signOut called');
    const cognitoUser = userPool.getCurrentUser();

    if (cognitoUser) {
      cognitoUser.signOut();
    }

    cachedAccessToken = null;
    cachedIdToken = null;
    this.clearToken();
    this.clearSelectedAccount();
  },

  validateAccountDomain(): boolean {
    const selectedAccount = this.getSelectedAccount();
    if (!selectedAccount) {
      return true;
    }

    const hostnameAccountName = getAccountNameFromHostname();
    if (!hostnameAccountName) {
      return true;
    }

    if (selectedAccount.accountName !== hostnameAccountName) {
      Logger.warn(
        `[Auth][TokenManager] validateAccountDomain: mismatch! selected=${selectedAccount.accountName} hostname=${hostnameAccountName} → clearing & reloading`,
      );
      this.clearSelectedAccount();
      window.location.reload();
      return false;
    }

    return true;
  },
};

export default CognitoTokenManager;
